/*
 * Where a tool's floating options sit (docs/ANALYSIS_WORKBENCH.md section 4b):
 * over the stage's bottom edge, centred on the tool's button in the toolbar
 * below, never past the stage's sides -- and lifted above an open drawer it
 * would otherwise cover. Pure: rectangles in, a place out (no DOM).
 *
 *   placeFloat({ root, stage, anchor, size, blockers, gap })
 *
 * Every rectangle is {left, top, right, bottom, width, height} in the
 * viewport's px (getBoundingClientRect). `anchor` is the button the options
 * belong to (null: the stage's middle), `size` the options' {width, height},
 * `blockers` the open drawers. The answer is relative to `root` (the
 * workbench, their containing block): {left, top, arrow, height} -- arrow the
 * px from the options' left edge to the anchor's middle, kept on the options
 * -- and `lifted`, true when a drawer pushed them up; null without a stage or
 * a size.
 */
export const FLOAT_GAP = 8;
// How close the arrow may come to the options' corners.
const ARROW_INSET = 14;

const finite = n => Number.isFinite(n);

export function placeFloat({ root, stage, anchor = null, size, blockers = [], gap = FLOAT_GAP } = {}) {
  if (!root || !stage || !size || !finite(size.width) || !finite(size.height)) return null;
  const w = size.width;
  const h = size.height;
  const centre = anchor && finite(anchor.left) && finite(anchor.width)
    ? anchor.left + anchor.width / 2 : stage.left + stage.width / 2;
  const minLeft = stage.left + gap;
  const maxLeft = stage.right - gap - w;
  // Wider than the stage: centred on it, whatever the anchor.
  const left = maxLeft < minLeft
    ? stage.left + (stage.width - w) / 2
    : Math.min(Math.max(centre - w / 2, minLeft), maxLeft);

  // Its bottom edge: the stage's, less the gap; then above every drawer it would cover, until none does.
  let bottom = stage.bottom - gap;
  for (let round = 0; round < blockers.length + 1; round += 1) {
    let moved = false;
    for (const b of blockers) {
      if (!b || !(b.width > 0) || !(b.height > 0)) continue;
      const acrossX = b.left < left + w && b.right > left;
      const acrossY = b.top < bottom && b.bottom > bottom - h;
      if (acrossX && acrossY) {
        bottom = b.top - gap;
        moved = true;
      }
    }
    if (!moved) break;
  }
  // Never above the stage's top: with no room left it stays there, over what it would cover.
  const top = Math.max(stage.top + gap, bottom - h);
  const arrow = Math.min(Math.max(centre - left, ARROW_INSET), Math.max(ARROW_INSET, w - ARROW_INSET));
  return {
    left: Math.round(left - root.left),
    top: Math.round(top - root.top),
    arrow: Math.round(arrow),
    height: Math.round(h),
    lifted: bottom < stage.bottom - gap,
  };
}

/** Two places the same (null for none). */
export const samePlace = (a, b) => (a === b) || (!!a && !!b && a.left === b.left && a.top === b.top
  && a.arrow === b.arrow && a.height === b.height && a.lifted === b.lifted);
